import { useEffect, useState, type ReactNode } from "react";
import { Clerk } from "@clerk/clerk-js";
import {
  ClerkFailed,
  ClerkLoaded,
  ClerkLoading,
  ClerkProvider,
} from "@clerk/react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { ui as clerkUi } from "@clerk/ui";
import { BRAND } from "@/lib/themes";

type ClerkStartupGateProps = {
  children: ReactNode;
};

type ClerkStartupProviderProps = {
  children: ReactNode;
  Clerk?: typeof Clerk;
  publishableKey?: string;
};

const CLERK_LOAD_ERROR_RE = /failed to load clerk js|failed_to_load_clerk_js/i;

const configuredPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
const defaultPublishableKey = configuredPublishableKey
  ? publishableKeyFromHost(window.location.hostname, configuredPublishableKey)
  : "";

export function isClerkLoadError(reason: unknown): boolean {
  const message =
    reason instanceof Error
      ? reason.message
      : typeof reason === "string"
        ? reason
        : "";
  return CLERK_LOAD_ERROR_RE.test(message);
}

export function ClerkStartupFailure() {
  return (
    <main
      data-testid="clerk-startup-fallback"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        padding: "1.5rem",
        backgroundColor: BRAND.offWhite,
        color: BRAND.charcoal,
        fontFamily:
          "'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <section
        style={{
          width: "min(100%, 32rem)",
          padding: "2.5rem 2rem",
          textAlign: "center",
          background: "#FFFFFF",
          border: "1px solid #E8E2D6",
          borderRadius: "1.5rem",
          boxShadow: "0 24px 60px -20px rgba(31, 27, 22, 0.18)",
        }}
      >
        <p
          style={{
            margin: "0 0 0.75rem",
            color: "var(--brand-sage)",
            fontSize: "0.7rem",
            fontWeight: 700,
            letterSpacing: "0.2em",
            textTransform: "uppercase",
          }}
        >
          Sign-in unavailable
        </p>
        <h1 style={{ margin: "0 0 0.875rem", fontSize: "1.875rem", lineHeight: 1.1 }}>
          We couldn&rsquo;t start sign-in.
        </h1>
        <p style={{ margin: "0 0 1.75rem", color: "#5A554D", lineHeight: 1.55 }}>
          The authentication service did not finish loading. Refresh to try
          again—your work is safe.
        </p>
        <button
          type="button"
          data-testid="button-clerk-retry"
          onClick={() => window.location.reload()}
          style={{
            padding: "0.75rem 1.5rem",
            border: 0,
            borderRadius: "9999px",
            background: BRAND.charcoal,
            color: BRAND.offWhite,
            cursor: "pointer",
            font: "inherit",
            fontWeight: 700,
          }}
        >
          Try again
        </button>
      </section>
    </main>
  );
}

export function ClerkStartupGate({ children }: ClerkStartupGateProps) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const onUnhandledRejection = (event: PromiseRejectionEvent) => {
      if (isClerkLoadError(event.reason)) {
        event.preventDefault();
        setFailed(true);
      }
    };
    const onWindowError = (event: ErrorEvent) => {
      if (isClerkLoadError(event.error ?? event.message)) {
        event.preventDefault();
        setFailed(true);
      }
    };

    window.addEventListener("unhandledrejection", onUnhandledRejection);
    window.addEventListener("error", onWindowError);
    return () => {
      window.removeEventListener("unhandledrejection", onUnhandledRejection);
      window.removeEventListener("error", onWindowError);
    };
  }, []);

  return failed ? <ClerkStartupFailure /> : children;
}

export function ClerkStartupProvider({
  children,
  Clerk: clerkConstructor = Clerk,
  publishableKey = defaultPublishableKey,
}: ClerkStartupProviderProps) {
  return (
    <ClerkProvider
      Clerk={clerkConstructor}
      ui={clerkUi}
      publishableKey={publishableKey}
      signInUrl="/sign-in"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
    >
      <ClerkLoading>
        <ClerkStartupLoading />
      </ClerkLoading>
      <ClerkFailed>
        <ClerkStartupFailure />
      </ClerkFailed>
      <ClerkLoaded>{children}</ClerkLoaded>
    </ClerkProvider>
  );
}

export function ClerkStartupLoading() {
  return (
    <main
      data-testid="clerk-startup-loading"
      aria-live="polite"
      style={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        backgroundColor: BRAND.offWhite,
        color: BRAND.charcoal,
        fontFamily:
          "'DM Sans', ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <p style={{ fontWeight: 700 }}>Starting secure sign-in…</p>
    </main>
  );
}